import { useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

export interface TaxYearSessionInfo {
  sessionId: string;
  kind: string | null;
  paymentStatus: string;
  liveMode: boolean;
  status: string;
  amountTotal: number | null;
  currency: string | null;
  taxYear: number | null;
  jurisdictionCode: string | null;
  tier: "starter" | "standard" | "pro_lite" | "trader" | "pro" | "concierge" | "concierge_complex" | "concierge_forensic" | null;
  addons: string[];
  dualNationalityCode: string | null;
  entitlementsGranted: number;
}

/**
 * Fetches a human-readable summary of a tax-year checkout session.
 * Polls every 2s for ~10s while entitlementsGranted == 0 to handle the
 * webhook race after redirect.
 */
export const useTaxYearSession = (sessionId: string | null | undefined) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["tax-year-session", sessionId],
    enabled: Boolean(sessionId),
    refetchInterval: (query) => {
      const data = query.state.data as TaxYearSessionInfo | undefined;
      if (!data) return 2000;
      if (data.entitlementsGranted > 0) return false;
      // Stop polling after ~10s
      if (query.state.dataUpdateCount > 5) return false;
      return 2000;
    },
    queryFn: async (): Promise<TaxYearSessionInfo> => {
      const token = await getToken();
      const response = await authorizedAxios(token).get(
        `/stripe/tax-year-session/${sessionId}`,
      );
      return response.data;
    },
  });
};
